import {Typography, Avatar, Box, Button, Fab} from '@mui/material';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {LoadingContent} from '../../../components/loading-content/loading-content';
import {UserQuickSignInInfo} from '../../../interfaces/user-quick-sign-in-info';
import BackspaceIcon from '@mui/icons-material/Backspace';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import {Modal} from '../../../components/modal/modal';

export default function QuickAccessLoginPinDrawer({
  selectedUser,
  state,
  onSubmit,
  onRemoveUser,
}: {
  selectedUser: UserQuickSignInInfo;
  state?: 'ready' | 'loading' | 'error' | string[];
  onSubmit?: (pin?: string) => void;
  onRemoveUser?: (userId: string) => void;
}) {
  const {t} = useTranslation();
  const stateToUse = state || 'ready';
  const [isDeletingRecent, setIsDeletingRecent] = useState<boolean>(false);
  const [isRegain, setIsRegain] = useState<boolean>(false);
  const [pin, setPin] = useState<string>('');

  const handlePinDigit = (digit: string) => {
    if (pin.length >= 4) {
      return;
    }

    const newPin = pin + digit;

    setPin(newPin);

    if (newPin.length === 4 && onSubmit) {
      onSubmit(newPin);
    }
  };

  if (isRegain) {
    return (
      <Box className="regain-content">
        <Typography className="title">
          {t(translations.quick_access_login.regain_access)}
        </Typography>
        <Typography className="message">
          {t(translations.quick_access_login.regain_access_message)}
        </Typography>
        <Typography className="message">
          {t(translations.quick_access_login.regain_access_description)}
        </Typography>
        <Button
          variant="text"
          className="button-text"
          sx={{
            color: 'var(--color-gray)',
          }}
          onClick={() => setIsRegain(false)}
        >
          {t(translations.quick_access_login.back_to_previous)}
        </Button>
      </Box>
    );
  }

  // Build
  return (
    <>
      <Box>
        <Avatar
          sx={{
            width: '85px',
            height: '85px',
          }}
          alt={selectedUser.name}
          src={selectedUser.picture}
        />
      </Box>
      <Typography className="title">
        {t(translations.quick_access_login.hello_user, {name: selectedUser.name})}
      </Typography>

      <Typography className="subtitle">
        {t(translations.quick_access_login.insert_your_quick_access_code)}
      </Typography>

      <Box className="pin-display">
        <Box
          className="digit"
          sx={{
            backgroundColor: pin.length >= 1 ? 'primary.main' : 'transparent',
          }}
        />
        <Box
          className="digit"
          sx={{
            backgroundColor: pin.length >= 2 ? 'primary.main' : 'transparent',
          }}
        ></Box>
        <Box
          className="digit"
          sx={{
            backgroundColor: pin.length >= 3 ? 'primary.main' : 'transparent',
          }}
        ></Box>
        <Box
          className="digit"
          sx={{
            backgroundColor: pin.length >= 4 ? 'primary.main' : 'transparent',
          }}
        ></Box>
      </Box>

      <Box className="keyboard-box">
        {stateToUse === 'ready' && (
          <Box className="pin-keyboard">
            <Box className="row">
              <Fab
                className="key"
                color="primary"
                aria-label="1"
                onClick={() => handlePinDigit('1')}
              >
                1
              </Fab>
              <Fab
                className="key"
                color="primary"
                aria-label="2"
                onClick={() => handlePinDigit('2')}
              >
                2
              </Fab>
              <Fab
                className="key"
                color="primary"
                aria-label="3"
                onClick={() => handlePinDigit('3')}
              >
                3
              </Fab>
            </Box>
            <Box className="row">
              <Fab
                className="key"
                color="primary"
                aria-label="4"
                onClick={() => handlePinDigit('4')}
              >
                4
              </Fab>
              <Fab
                className="key"
                color="primary"
                aria-label="5"
                onClick={() => handlePinDigit('5')}
              >
                5
              </Fab>
              <Fab
                className="key"
                color="primary"
                aria-label="6"
                onClick={() => handlePinDigit('6')}
              >
                6
              </Fab>
            </Box>
            <Box className="row">
              <Fab
                className="key"
                color="primary"
                aria-label="7"
                onClick={() => handlePinDigit('7')}
              >
                7
              </Fab>
              <Fab
                className="key"
                color="primary"
                aria-label="8"
                onClick={() => handlePinDigit('8')}
              >
                8
              </Fab>
              <Fab
                className="key"
                color="primary"
                aria-label="9"
                onClick={() => handlePinDigit('9')}
              >
                9
              </Fab>
            </Box>
            <Box className="row">
              <Fab
                className="special-key"
                color="error"
                aria-label="delete-remember"
                onClick={() => setIsDeletingRecent(true)}
              >
                <PersonRemoveIcon />
              </Fab>
              <Fab
                className="key"
                color="primary"
                aria-label="0"
                onClick={() => handlePinDigit('0')}
              >
                0
              </Fab>
              <Fab
                className="special-key"
                color="secondary"
                aria-label="delete-digit"
                onClick={() => {
                  if (pin.length <= 0) return;
                  setPin(pin.slice(0, -1));
                }}
              >
                <BackspaceIcon />
              </Fab>
            </Box>
          </Box>
        )}
        {stateToUse === 'loading' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <LoadingContent />
          </Box>
        )}
        {typeof stateToUse !== 'string' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              flexDirection: 'column',
            }}
          >
            <Typography className="error-title">
              {stateToUse.includes('user-affiliation-quick-access-wrong-code')
                ? t(translations.quick_access_login.login_failed_code)
                : t(translations.quick_access_login.login_failed)}
            </Typography>
            <Button
              variant="contained"
              sx={{
                marginTop: '32px',
                textTransform: 'initial',
              }}
              onClick={() => {
                setPin('');
                onSubmit && onSubmit();
              }}
            >
              {t(translations.quick_access_login.try_again)}
            </Button>
            <Button
              variant="text"
              className="button-text"
              sx={{
                marginTop: '28px',
              }}
              onClick={() => setIsRegain(true)}
            >
              {t(translations.quick_access_login.regain_access)}
            </Button>
          </Box>
        )}
      </Box>
      {isDeletingRecent && (
        <Modal
          open
          className="delete-modal"
          maxWidth="sm"
          fullWidth
          onClose={() => setIsDeletingRecent(false)}
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: () => setIsDeletingRecent(false),
            },
            {
              label: t(translations.modal.confirm),
              type: 'nondestructive',
              onClick: () => {
                if (onRemoveUser) onRemoveUser(selectedUser.id);
                setIsDeletingRecent(false);
              },
            },
          ]}
        >
          <Typography className="delete-modal-title">
            {t(translations.quick_access_login.remove_recent)}
          </Typography>
        </Modal>
      )}
    </>
  );
}
